// Object state rule for «Карта объектов» (spec §7, amended 2026-10-06). Pure functions over an enriched location.

export const UNGROUPED_COLOR = '#8b918c';
export const RING_COLOR = '#171a19';
export const SELECTED_COLOR = '#17876f';
export const GROUP_PALETTE = Object.freeze(['#17876f', '#d08b1f', '#2f6fb3', '#c0492b', '#6b4fa8']);

// Filter chips, in rising severity. The marker takes the colour of the worst flag the object has
// (`resolveObjectState`); the chips are independent flags (`objectFlags`). Sleep purple is kept
// apart from the group palette's violet (#6b4fa8) so custom points never read as sleeping objects.
export const OBJECT_STATES = Object.freeze([
  { key: 'online', label: 'Онлайн', color: '#17876f' },
  { key: 'sleeping', label: 'Спит', color: '#8d5bdc' },
  { key: 'service', label: 'Требуется обслуживание', color: '#d08b1f' },
  { key: 'error', label: 'Критическая ошибка', color: '#c0492b' },
  { key: 'offline', label: 'Офлайн', color: '#6b7078' },
  { key: 'unknown', label: 'Нет данных', color: '#b6bcb6' },
]);

/** Marker colour priority (worst first); `unknown` never mixes with the others. */
const SEVERITY = Object.freeze(['offline', 'error', 'service', 'sleeping', 'online']);

export const STATE_BY_KEY = Object.freeze(
  OBJECT_STATES.reduce((acc, s) => ({ ...acc, [s.key]: s }), {})
);

const TONER_KEYS = [
  ['C', 'cyan_percent'],
  ['M', 'magenta_percent'],
  ['Y', 'yellow_percent'],
  ['K', 'black_percent'],
];

const trim = (v) => String(v == null ? '' : v).trim();
const present = (v) => v !== null && v !== undefined && trim(v) !== '';
const stringList = (v) => (Array.isArray(v) ? v : []).map((s) => trim(s)).filter(Boolean);

export function anchorPrinter(location) {
  const printers = (location && location.equipment && Array.isArray(location.equipment.printers))
    ? location.equipment.printers
    : [];
  if (printers.length === 0) return null;
  const serial = trim(location.info_anchor_printer_serial_number);
  if (serial) {
    const match = printers.find((p) => (
      trim(p && p.printer && p.printer.serial_number) === serial
      || trim(p && p.unit && p.unit.serial_number) === serial
    ));
    if (match) return match;
  }
  return printers[0] || null; // documented fallback only
}

/** The backend's human-readable diagnosis (`PrinterStateUtils`). */
function diagnosis(entry) {
  const printer = entry && entry.printer;
  return {
    errors: stringList(printer && printer.errors),
    warnings: stringList(printer && printer.warnings),
  };
}

function connectivityOf(location) {
  const p = anchorPrinter(location);
  const s = p && p.state;
  if (!s || typeof s !== 'object') return { entry: null, key: 'unknown' };
  if (s.connectivity === 'offline') return { entry: p, key: 'offline' };
  if (s.connectivity === 'online') return { entry: p, key: 'online' };
  return { entry: p, key: 'unknown' };
}

/**
 * Every flag an object carries (spec §7, amended 2026-10-07): the connectivity (`online` /
 * `offline` / `unknown`) first, then `error` for a non-empty `printer.errors`, `service` for a
 * non-empty `printer.warnings`, and `sleeping` for an online printer that reports sleep. An
 * offline printer keeps its last error / service lists; `unknown` stands alone. The raw snapshot
 * (faults, toner, trays) is never re-interpreted here.
 */
export function objectFlags(location) {
  const { entry, key } = connectivityOf(location);
  if (key === 'unknown') return ['unknown'];
  const flags = [key];
  const d = diagnosis(entry);
  if (d.errors.length > 0) flags.push('error');
  if (d.warnings.length > 0) flags.push('service');
  if (key === 'online' && entry.printer && entry.printer.sleeping === true) flags.push('sleeping');
  return flags;
}

/** The worst flag — one colour per marker: offline > error > service > sleeping > online. */
export function resolveObjectState(location) {
  const flags = objectFlags(location);
  return SEVERITY.find((k) => flags.includes(k)) || 'unknown';
}

export function stateMeta(location) {
  return STATE_BY_KEY[resolveObjectState(location)];
}

export function tonerLevels(location) {
  const p = anchorPrinter(location);
  const toner = p && p.state && p.state.toner;
  if (!toner || typeof toner !== 'object') return null;
  return TONER_KEYS.map(([key, field]) => {
    const raw = toner[field];
    const n = present(raw) ? Number(raw) : NaN;
    return { key, percent: Number.isFinite(n) ? n : null };
  });
}

const URL_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;

/** Anchor printer's web UI: `printer.ip` (+ `:port`), over https unless the ip already carries a scheme. */
export function printerInterfaceUrl(location) {
  const p = anchorPrinter(location);
  const ip = trim(p && p.printer && p.printer.ip);
  if (!ip) return null;
  const port = trim(p && p.printer && p.printer.port);
  const base = URL_SCHEME.test(ip) ? ip : `https://${ip}`;
  return port ? `${base}:${port}` : base;
}

/**
 * The «Проблемы» block: the backend's `errors` (critical) and `warnings` (service) for any
 * printer with data, online or offline; `null` when there is no printer data at all.
 */
export function problemLists(location) {
  const { entry, key } = connectivityOf(location);
  if (key === 'unknown') return null;
  return diagnosis(entry);
}

export function toLatLng(obj) {
  if (!obj || !present(obj.latitude) || !present(obj.longitude)) return null;
  const lat = Number(obj.latitude);
  const lng = Number(obj.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return [lat, lng];
}

export function hasValidCoords(obj) {
  return toLatLng(obj) !== null;
}

const BASIC_COLOR_NAMES = ['red', 'blue', 'green', 'orange', 'yellow', 'purple', 'black', 'white', 'gray', 'grey',
  'brown', 'pink', 'teal', 'navy', 'olive', 'maroon', 'lime', 'cyan', 'magenta', 'violet', 'indigo', 'gold', 'silver'];

// Strict allowlist, never `CSS.supports`: browsers accept `var(--x, <any tokens>)` for any property, so a
// group colour could carry a quote and break out of a `style="…"` attribute (stored XSS). Same rule in
// every engine, so the test environment validates exactly what the browser does.
const HEX_COLOR = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_COLOR = /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\)$/;
const HSL_COLOR = /^hsla?\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*(,\s*(0|1|0?\.\d+)\s*)?\)$/;

function isCssColor(value) {
  const v = trim(value);
  if (!v) return false;
  return HEX_COLOR.test(v) || RGB_COLOR.test(v) || HSL_COLOR.test(v) || BASIC_COLOR_NAMES.includes(v.toLowerCase());
}

export function groupColor(group) {
  const value = trim(group && group.color_code);
  return value && isCssColor(value) ? value : UNGROUPED_COLOR;
}
